import { useState } from "react";
import { Loader2, Pencil, Volume2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import TempoKeyEditor from "@/components/tempo/TempoKeyEditor";
import { NOTES, keyName, noteContext, playKeyNote } from "@/lib/tempoKey";

const pill = "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider";
const btn = `${pill} border-white/15 bg-white/[0.04] text-white/80 transition-colors duration-200 hover:bg-white/[0.12] hover:text-white`;

/** Tempo + key of the music, the starting note to find your pitch, and a Fix button to correct either one. */
export default function TempoKeyChip({ tempo, detecting, onChange, onRedetect, getSongTime, getCtx = noteContext }) {
  const [open, setOpen] = useState(false);
  if (detecting && !tempo) {
    return <p data-testid="tempo-detecting" className="flex items-center gap-2 text-[12px] text-white/60"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding the tempo and key…</p>;
  }
  const hasKey = tempo?.tonic != null;
  return (
    <div data-testid="tempo-key-chip" data-source={tempo?.source || "none"} className="flex flex-wrap items-center gap-1.5">
      {tempo ? (
        <>
          <span data-testid="tempo-bpm" className={`${pill} border-sky-300/40 bg-sky-500/15 text-sky-50`}>{Math.round(tempo.bpm * 10) / 10} BPM</span>
          {hasKey ? <span data-testid="tempo-key" className={`${pill} border-amber-300/40 bg-amber-500/15 normal-case text-amber-50`}>{keyName(tempo)}</span> : null}
          {hasKey ? (
            <button type="button" data-testid="tempo-play-note" onClick={() => playKeyNote(getCtx(), tempo.tonic)} className={btn} title="Hear the key's home note">
              <Volume2 className="h-3.5 w-3.5" /> <span className="normal-case">{NOTES[tempo.tonic]}</span> note
            </button>
          ) : null}
          <span data-testid="tempo-source" className="font-mono text-[9px] uppercase tracking-[0.16em] text-white/40">{detecting ? "Checking…" : tempo.source === "manual" ? "Set by you" : "Auto"}</span>
        </>
      ) : <span data-testid="tempo-none" className="text-[12px] text-white/50">Tempo and key not set</span>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid="tempo-edit" className={btn}><Pencil className="h-3 w-3" /> {tempo ? "Fix" : "Set"}</button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 border-white/10 bg-[#0b0e1c] text-white">
          <TempoKeyEditor tempo={tempo} onChange={onChange} detecting={detecting} getSongTime={getSongTime}
            onRedetect={onRedetect ? () => { setOpen(false); onRedetect(); } : null} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
